import React, { useContext, useMemo } from "react";
import { Route as RouterRoute, Redirect } from "react-router-dom";

import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import useTurnosChatAccess from "../hooks/useTurnosChatAccess";
import { isTurnosProfile } from "../constants/turnosProfiles";
import useWaiaEnviosMode, {
  WAIA_ENVIOS_PATHS,
  WAIA_ENVIOS_HOME,
} from "../hooks/useWaiaEnviosMode";

// Pages superadmin should NOT access (company-scoped)
const ADMIN_ONLY_PATHS = ["/", "/tickets", "/connections", "/contacts", "/users", "/quickAnswers", "/Settings", "/Queues", "/crm", "/soporte"];

// Rutas de Chats que profesional/agendador pueden abrir SOLO en la versión full (con_chat).
const CHAT_PATHS = ["/tickets", "/tickets/:ticketId?"];

// feat_350 — Pages que exigen profile "admin" DE EMPRESA. Array paralelo a
// ADMIN_ONLY_PATHS: aquél saca al superadmin de las pantallas de empresa, éste
// saca al que NO es admin de las pantallas de administración.
//
// ⚠ Este guard NO vive en el cuerpo del componente como los otros 4, vive en el
// `component` que recibe <RouterRoute>. La razón (no "simplificar" esto):
//
//   a) El <Switch> de routes/index.js elige SettingsProvider (no tiene prop
//      `path` → matchea siempre) para cualquier URL que no sea login/signup/
//      sala/s. O sea que las ~34 <Route> de adentro se montan TODAS A LA VEZ,
//      estés en la URL que estés, y cada una corre el cuerpo entero.
//   b) <Redirect> de react-router 5 navega en onMount, y en onUpdate sólo si
//      `to` cambió. Por eso los 4 guards del cuerpo funcionan: son
//      CONVERGENTES (mandan todo a un único home) y disparan una sola vez.
//   c) Route.js no consume el contexto del router → NO re-renderiza al navegar.
//
//   Una lista negra de UN path no es convergente: puesta en el cuerpo,
//   dispararía al montar el layout y no al pedir /settings — un profile="user"
//   quedaría arrancado de su home al hacer login.
//
//   La tentación es useLocation() + matchPath para scopear a la URL activa.
//   NO: useLocation suscribe Route.js al contexto del router y las ~34 rutas
//   pasan a re-renderizar en cada navegación (hoy ninguna lo hace). Cambio de
//   render global para cerrar una puerta puntual.
//
//   El `component` de <RouterRoute> ya está scopeado gratis: sólo renderiza
//   cuando el path matchea la URL. Ahí va el guard.
const ADMIN_PROFILE_PATHS = ["/Settings", "/soporte"];

const Route = ({ component: Component, isPrivate = false, ...rest }) => {
  const { isAuth, loading, user } = useContext(AuthContext);
  const turnosChat = useTurnosChatAccess(user);
  const waiaEnviosMode = useWaiaEnviosMode(user);

  const profile = user?.profile;
  const needsAdminProfile =
    isAuth && isPrivate && ADMIN_PROFILE_PATHS.includes(rest.path);

  // ⚠ El useMemo NO es opcional: sin él `Guarded` sería una función nueva en
  // cada render, React la trataría como otro tipo de componente y REMONTARÍA el
  // árbol entero de /Settings — se perderían los ~60 useState del shell de
  // tabs (feat_347) en cada re-render del padre.
  const Guarded = useMemo(
    () =>
      needsAdminProfile
        ? props =>
            profile === "admin" ? (
              <Component {...props} />
            ) : (
              <Redirect to="/" />
            )
        : Component,
    [Component, needsAdminProfile, profile]
  );

  if (!isAuth && isPrivate) {
    return (
      <>
        {loading && <BackdropLoading />}
        <Redirect to={{ pathname: "/login", state: { from: rest.location } }} />
      </>
    );
  }

  if (isAuth && !isPrivate) {
    return (
      <>
        {loading && <BackdropLoading />}
        <Redirect to={{ pathname: user?.isSuperAdmin ? "/superadmin" : "/", state: { from: rest.location } }} />;
      </>
    );
  }

  // Redirect profesional/agendador to /turnos if accessing any other page.
  // Excepción: en la versión full (con_chat) pueden abrir la sección Chats (/tickets).
  if (isAuth && isPrivate && isTurnosProfile(user?.profile) && rest.path !== "/turnos") {
    const chatAllowed = turnosChat.enabled && CHAT_PATHS.includes(rest.path);
    if (!chatAllowed) {
      return (
        <>
          {loading && <BackdropLoading />}
          <Redirect to="/turnos" />
        </>
      );
    }
  }

  // feat_333 — Modo WAIAenvios: ocultar el ítem del menú NO bloquea la URL.
  // Con el flag en "true" solo resuelven /connections y /carga-de-datos; todo
  // lo demás (incluidos "/" y "/dashboard", que hoy son pages/Reports y en este
  // modo quedarían vacíos) redirige a /carga-de-datos.
  // Las rutas de sesión (/login, /signup, /sala/..., /s/:code) no entran acá:
  // son isPrivate={false} Y viven fuera del SettingsProvider.
  // ⚠ Este gate es de UX, no de seguridad: el backend sigue respondiendo a
  // cualquier isAuth (gate de backend = fase 2, todo_056). Además, mientras el
  // GET /settings está en vuelo el modo se lee apagado, así que la página no
  // permitida puede alcanzar a pintarse un instante antes del redirect.
  // ⚠ profesional/agendador quedan FUERA a propósito. Su gate (arriba) manda
  // todo a /turnos, que no está en WAIA_ENVIOS_PATHS: los dos gates juntos
  // harían /turnos -> /carga-de-datos -> /turnos en bucle infinito. Un perfil
  // de turnos en una empresa WAIAenvios conserva su gate y su menú, igual que
  // en MainListItems.
  if (
    isAuth &&
    isPrivate &&
    waiaEnviosMode &&
    !isTurnosProfile(user?.profile) &&
    !WAIA_ENVIOS_PATHS.includes(rest.path)
  ) {
    return (
      <>
        {loading && <BackdropLoading />}
        <Redirect to={WAIA_ENVIOS_HOME} />
      </>
    );
  }

  // Redirect superadmin away from company-scoped pages
  if (isAuth && isPrivate && user?.isSuperAdmin && ADMIN_ONLY_PATHS.includes(rest.path)) {
    return (
      <>
        {loading && <BackdropLoading />}
        <Redirect to="/superadmin" />
      </>
    );
  }

  return (
    <>
      {loading && <BackdropLoading />}
      <RouterRoute {...rest} component={Guarded} />
    </>
  );
};

export default Route;
